import { ChangeDetectionStrategy, Component, output, signal } from '@angular/core';
import { BfmActiveFiltersComponent } from '@celerofinancas/ui-cash-flow';

/**
 * Filters header of the "Todos os registros" tab — search by name, a summary of
 * the applied filters and the button that opens the modal.
 *
 * Equivalent to `financial-list-filters` in `serasa-cl-webcomponents`, which is
 * the pattern rule 4 of card
 * [86e2v9afr](https://app.clickup.com/t/86e2v9afr) requires following.
 *
 * ## The count comes from the package, not from a local dictionary
 *
 * Serasa keeps a list of keys (`cost_center_pk`, `has_document`, …) and counts how
 * many are filled. That duplicates knowledge: `bfm-active-filters` already walks
 * the applied parameters to build the chips and emits `activeFiltersCount` with
 * the total. Using the package output keeps the number and the chips in agreement
 * — with the local dictionary they diverge the day the package starts showing a
 * new chip.
 *
 * ## Search does not count
 *
 * It has its own affordance (the field shows the typed term and the "x" to clear
 * it), and `bfm-active-filters` ignores it too. Counting it would make the badge
 * say "1 filter" with the modal completely empty.
 */
@Component({
  selector: 'financial-list-filters',
  standalone: true,
  imports: [BfmActiveFiltersComponent],
  templateUrl: './financial-list-filters.component.html',
  styleUrls: ['./financial-list-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialListFiltersComponent {
  /**
   * Confirmed search term (Enter or a click on the magnifier).
   */
  public readonly submitSearch = output<string>();

  /**
   * Request to open the filters modal.
   */
  public readonly openFilters = output<void>();

  /**
   * Term currently typed in the search field.
   */
  public readonly searchTerm = signal('');

  /**
   * Whether the current search has been submitted. Drives swapping the magnifier
   * button for the "x": while the user types without confirming, the magnifier
   * stays there so they can submit; once submitted, what makes sense to offer is
   * clearing.
   */
  public readonly isSubmitted = signal(false);

  /**
   * How many filters are applied, shown as a badge next to "Filtros".
   */
  public readonly filtersCount = signal(0);

  /**
   * Keeps the term in sync as the user types and drops the submitted state.
   *
   * @param event input event coming from the search field
   */
  public onInput(event: Event): void {
    if (this.isSubmitted()) {
      this.isSubmitted.set(false);
    }

    if (event.target instanceof HTMLInputElement) {
      this.searchTerm.set(event.target.value);
    }
  }

  /**
   * Confirms the search, emitting the current term.
   */
  public onSubmit(): void {
    this.submitSearch.emit(this.searchTerm());
    this.isSubmitted.set(true);
  }

  /**
   * Clears the field and emits an empty search, which reopens the full list.
   */
  public onClear(): void {
    this.searchTerm.set('');
    this.isSubmitted.set(false);
    this.submitSearch.emit('');
  }

  /**
   * Takes the applied-filter count the chip list reports.
   *
   * @param count number of filters currently applied
   */
  public onFiltersCount(count: number): void {
    this.filtersCount.set(count);
  }
}
